import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  adminGetStats, adminGetBookings, adminGetUsers, adminGetGallery, adminGetActivities,
  adminGetSettings, adminUpdateSettings, adminDeleteUser, adminDeleteBooking,
  adminAddGalleryImage, adminDeleteGalleryImage, adminUpdateBooking, adminUpdateUser,
  adminCreateActivity, adminUpdateActivity, adminDeleteActivity,
  adminGetAmenities, adminCreateAmenity, adminUpdateAmenity, adminDeleteAmenity
} from '../services/api';
import {
  TreePine, Users, Calendar, Image, Activity, Settings, BarChart3,
  LogOut, Loader2, AlertCircle, Check, X, Plus, Edit2, Trash2,
  DollarSign, Home, ChevronRight, Eye, EyeOff, Save, Package
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [activities, setActivities] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/login');
      } else if (user?.role !== 'admin') {
        navigate('/dashboard');
      } else {
        fetchData();
      }
    }
  }, [isAuthenticated, authLoading, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, bookingsData, usersData, galleryData, activitiesData, amenitiesData, settingsData] = await Promise.all([
        adminGetStats(),
        adminGetBookings(),
        adminGetUsers(),
        adminGetGallery(),
        adminGetActivities(),
        adminGetAmenities(),
        adminGetSettings()
      ]);
      setStats(statsData);
      setBookings(bookingsData);
      setUsers(usersData);
      setGallery(galleryData);
      setActivities(activitiesData);
      setAmenities(amenitiesData);
      setSettings(settingsData);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/dashboard');
      } else {
        setError('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'activities', label: 'Activities', icon: Activity },
    { id: 'amenities', label: 'Amenities', icon: Package },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-950 border-r border-stone-800 flex flex-col">
        <div className="p-6 border-b border-stone-800">
          <Link to="/" className="flex items-center gap-2">
            <TreePine className="w-8 h-8 text-emerald-400" />
            <span className="text-lg font-semibold text-white">
              Admin Panel
            </span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'text-stone-400 hover:bg-stone-800 hover:text-white'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-800">
          <Link to="/" className="flex items-center gap-3 px-4 py-3 text-stone-400 hover:text-white rounded-lg hover:bg-stone-800 transition-colors">
            <Home className="w-5 h-5" />
            View Site
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-stone-400 hover:text-red-400 rounded-lg hover:bg-stone-800 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-stone-900/95 border-b border-stone-800 px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white capitalize">{activeTab}</h1>
            <span className="text-stone-400">Welcome, {user?.name}</span>
          </div>
        </header>

        <div className="px-8 pt-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400">{error}</p>
              <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4 text-red-400" /></button>
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-3">
              <Check className="w-5 h-5 text-emerald-400" />
              <p className="text-emerald-400">{success}</p>
              <button onClick={() => setSuccess('')} className="ml-auto"><X className="w-4 h-4 text-emerald-400" /></button>
            </div>
          )}
        </div>

        <div className="p-8">
          {activeTab === 'overview' && <OverviewTab stats={stats} bookings={bookings} />}
          {activeTab === 'bookings' && <BookingsTab bookings={bookings} setBookings={setBookings} setError={setError} setSuccess={setSuccess} />}
          {activeTab === 'users' && <UsersTab users={users} setUsers={setUsers} setError={setError} setSuccess={setSuccess} currentUserId={user?.user_id} />}
          {activeTab === 'gallery' && <GalleryTab gallery={gallery} setGallery={setGallery} setError={setError} setSuccess={setSuccess} />}
          {activeTab === 'activities' && <ActivitiesTab activities={activities} setActivities={setActivities} setError={setError} setSuccess={setSuccess} />}
          {activeTab === 'amenities' && <AmenitiesTab amenities={amenities} setAmenities={setAmenities} setError={setError} setSuccess={setSuccess} />}
          {activeTab === 'settings' && <SettingsTab settings={settings} setSettings={setSettings} setError={setError} setSuccess={setSuccess} gallery={gallery} />}
        </div>
      </main>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ stats, bookings }) => {
  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400' },
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: Calendar, color: 'text-emerald-400' },
    { label: 'Upcoming', value: stats?.upcoming_bookings || 0, icon: Calendar, color: 'text-amber-400' },
    { label: 'Total Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-green-400' },
  ];

  const upcomingBookings = bookings
    .filter(b => new Date(b.check_in) >= new Date() && b.status === 'confirmed')
    .slice(0, 5);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div key={stat.label} className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-stone-400 text-sm">{stat.label}</span>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div className="text-3xl font-bold text-white">{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Upcoming Bookings</h3>
        {upcomingBookings.length === 0 ? (
          <p className="text-stone-400">No upcoming bookings</p>
        ) : (
          <div className="space-y-3">
            {upcomingBookings.map((booking) => (
              <div key={booking.booking_id} className="flex items-center justify-between p-4 bg-stone-900/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">{booking.user_name || booking.guest_name || 'Guest'}</p>
                  <p className="text-stone-400 text-sm">
                    {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-emerald-400 font-semibold">${booking.total_price}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Bookings Tab
const BookingsTab = ({ bookings, setBookings, setError, setSuccess }) => {
  const [filter, setFilter] = useState('all');

  const filteredBookings = filter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === filter);

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await adminUpdateBooking(bookingId, { status: newStatus });
      setBookings(bookings.map(b => b.booking_id === bookingId ? { ...b, status: newStatus } : b));
      setSuccess('Booking status updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update booking');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await adminDeleteBooking(bookingId);
      setBookings(bookings.filter(b => b.booking_id !== bookingId));
      setSuccess('Booking deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete booking');
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {['all', 'confirmed', 'cancelled', 'completed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
              filter === f ? 'bg-emerald-600 text-white' : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-900/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Guest</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Dates</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Nights</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Total</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Status</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-700/50">
            {filteredBookings.map((booking) => (
              <tr key={booking.booking_id} className="hover:bg-stone-800/50">
                <td className="px-6 py-4">
                  <p className="text-white font-medium">{booking.user_name || booking.guest_name || 'N/A'}</p>
                  <p className="text-stone-400 text-sm">{booking.user_email || booking.guest_email || ''}</p>
                </td>
                <td className="px-6 py-4 text-stone-300">
                  {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-stone-300">{booking.nights}</td>
                <td className="px-6 py-4 text-emerald-400 font-semibold">${booking.total_price}</td>
                <td className="px-6 py-4">
                  <select
                    value={booking.status}
                    onChange={(e) => handleStatusChange(booking.booking_id, e.target.value)}
                    className="bg-stone-700 text-white text-sm rounded px-2 py-1 border-0"
                  >
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                  </select>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => handleDelete(booking.booking_id)} className="text-red-400 hover:text-red-300 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredBookings.length === 0 && (
          <div className="text-center py-8 text-stone-400">No bookings found</div>
        )}
      </div>
    </div>
  );
};

// Users Tab
const UsersTab = ({ users, setUsers, setError, setSuccess, currentUserId }) => {
  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminUpdateUser(userId, { role: newRole });
      setUsers(users.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      setSuccess('User role updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await adminDeleteUser(userId);
      setUsers(users.filter(u => u.user_id !== userId));
      setSuccess('User deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-stone-900/50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">User</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Email</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Provider</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Role</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Joined</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-700/50">
          {users.map((user) => (
            <tr key={user.user_id} className="hover:bg-stone-800/50">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Users className="w-4 h-4 text-emerald-400" />
                    </div>
                  )}
                  <span className="text-white font-medium">{user.name}</span>
                </div>
              </td>
              <td className="px-6 py-4 text-stone-300">{user.email}</td>
              <td className="px-6 py-4 text-stone-400 capitalize">{user.auth_provider}</td>
              <td className="px-6 py-4">
                <select
                  value={user.role}
                  onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
                  disabled={user.user_id === currentUserId}
                  className="bg-stone-700 text-white text-sm rounded px-2 py-1 border-0 disabled:opacity-50"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td className="px-6 py-4 text-stone-400 text-sm">
                {new Date(user.created_at).toLocaleDateString()}
              </td>
              <td className="px-6 py-4">
                {user.user_id !== currentUserId && (
                  <button onClick={() => handleDelete(user.user_id)} className="text-red-400 hover:text-red-300 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Gallery Tab
const GalleryTab = ({ gallery, setGallery, setError, setSuccess }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newImage, setNewImage] = useState({ url: '', caption: '', category: 'general' });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setNewImage({ ...newImage, url: data.url });
      setSuccess('Image uploaded successfully!');
    } catch (err) {
      setError('Failed to upload image: ' + err.message);
    }
  };

  const handleAdd = async () => {
    if (!newImage.url) {
      setError('Image URL is required');
      return;
    }
    try {
      const added = await adminAddGalleryImage(newImage);
      setGallery([...gallery, added]);
      setNewImage({ url: '', caption: '', category: 'general' });
      setShowAdd(false);
      setSuccess('Image added successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add image');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await adminDeleteGalleryImage(imageId);
      setGallery(gallery.filter(img => img.image_id !== imageId));
      setSuccess('Image deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete image');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-white">
          Gallery Images ({gallery.length})
        </h3>
        <Button
          onClick={() => setShowAdd(true)}
          className="bg-emerald-600 hover:bg-emerald-500"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Image
        </Button>
      </div>

      {showAdd && (
        <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-6">
          <h4 className="text-white font-medium mb-4">Add New Image</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-stone-400">Image URL or Upload</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newImage.url}
                  onChange={(e) =>
                    setNewImage({ ...newImage, url: e.target.value })
                  }
                  placeholder="https://... or upload below"
                  className="bg-stone-900 border-stone-600 text-white flex-1"
                />
                <Label className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-md cursor-pointer transition-colors">
                  Upload image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </Label>
              </div>
            </div>
            <div>
              <Label className="text-stone-400">Caption (optional)</Label>
              <Input
                value={newImage.caption}
                onChange={(e) =>
                  setNewImage({ ...newImage, caption: e.target.value })
                }
                placeholder="Image description"
                className="bg-stone-900 border-stone-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-stone-400">Category</Label>
              <select
                value={newImage.category}
                onChange={(e) =>
                  setNewImage({ ...newImage, category: e.target.value })
                }
                className="w-full mt-1 bg-stone-900 border border-stone-600 text-white rounded-md px-3 py-2"
              >
                <option value="general">General</option>
                <option value="cabin">Cabin</option>
                <option value="nature">Nature</option>
                <option value="activities">Activities</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleAdd}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              Add Image
            </Button>
            <Button
              onClick={() => setShowAdd(false)}
              variant="outline"
              className="border-stone-600 text-stone-300"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {gallery.map((image) => (
          <div
            key={image.image_id}
            className="relative group rounded-xl overflow-hidden"
          >
            <img
              src={image.url}
              alt={image.caption}
              className="w-full h-40 object-cover"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                onClick={() => handleDelete(image.image_id)}
                className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
              <p className="text-white text-xs truncate">
                {image.caption || 'No caption'}
              </p>
              <p className="text-stone-400 text-xs capitalize">
                {image.category}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// Activities Tab - Enhanced with Add/Edit functionality
const ActivitiesTab = ({ activities, setActivities, setError, setSuccess }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '', icon: '', short_description: '', full_description: '', season: 'Year-round', image_url: ''
  });

  const iconOptions = ['Fish', 'Target', 'Ship', 'Waves', 'Mountain', 'Flame', 'Binoculars', 'Snowflake', 'Tent', 'TreePine', 'Bird', 'Camera', 'Compass', 'Map'];

  const resetForm = () => {
    setForm({ name: '', icon: '', short_description: '', full_description: '', season: 'Year-round', image_url: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (activity) => {
    setForm({
      name: activity.name,
      icon: activity.icon,
      short_description: activity.short_description,
      full_description: activity.full_description,
      season: activity.season,
      image_url: activity.image_url || ''
    });
    setEditingId(activity.activity_id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.icon || !form.short_description) {
      setError('Name, icon, and short description are required');
      return;
    }
    try {
      if (editingId) {
        const updated = await adminUpdateActivity(editingId, form);
        setActivities(activities.map(a => a.activity_id === editingId ? updated : a));
        setSuccess('Activity updated');
      } else {
        const created = await adminCreateActivity(form);
        setActivities([...activities, created]);
        setSuccess('Activity created');
      }
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save activity');
    }
  };

  const handleToggle = async (activityId, isActive) => {
    try {
      await adminUpdateActivity(activityId, { is_active: !isActive });
      setActivities(activities.map(a => a.activity_id === activityId ? { ...a, is_active: !isActive } : a));
      setSuccess('Activity updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update activity');
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await adminDeleteActivity(activityId);
      setActivities(activities.filter(a => a.activity_id !== activityId));
      setSuccess('Activity deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete activity');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-white">Activities ({activities.length})</h3>
        <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-500">
          <Plus className="w-4 h-4 mr-2" /> Add Activity
        </Button>
      </div>

      {showForm && (
        <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-6">
          <h4 className="text-white font-medium mb-4">{editingId ? 'Edit Activity' : 'Add New Activity'}</h4>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-stone-400">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
            </div>
            <div>
              <Label className="text-stone-400">Icon *</Label>
              <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="w-full mt-1 bg-stone-900 border border-stone-600 text-white rounded-md px-3 py-2">
                <option value="">Select icon...</option>
                {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-stone-400">Season</Label>
              <Input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
            </div>
            <div>
              <Label className="text-stone-400">Image URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-stone-400">Short Description *</Label>
            <Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div className="mb-4">
            <Label className="text-stone-400">Full Description</Label>
            <Textarea value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} rows={4} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-500">
              <Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Create'}
            </Button>
            <Button onClick={resetForm} variant="outline" className="border-stone-600 text-stone-300">Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-900/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Activity</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Season</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Status</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-stone-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-700/50">
            {activities.map((activity) => (
              <tr key={activity.activity_id} className="hover:bg-stone-800/50">
                <td className="px-6 py-4">
                  <p className="text-white font-medium">{activity.name}</p>
                  <p className="text-stone-400 text-sm truncate max-w-md">{activity.short_description}</p>
                </td>
                <td className="px-6 py-4 text-stone-300">{activity.season}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleToggle(activity.activity_id, activity.is_active)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      activity.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-stone-600 text-stone-400'
                    }`}
                  >
                    {activity.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(activity)} className="text-blue-400 hover:text-blue-300 p-1">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(activity.activity_id)} className="text-red-400 hover:text-red-300 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {activities.length === 0 && (
          <div className="text-center py-8 text-stone-400">No activities yet. Add your first activity!</div>
        )}
      </div>
    </div>
  );
};

// Amenities Tab - For About section
const AmenitiesTab = ({ amenities, setAmenities, setError, setSuccess }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', icon: '' });

  const iconOptions = ['UtensilsCrossed', 'Flame', 'Anchor', 'Wifi', 'Beef', 'Tent', 'Users', 'PawPrint', 'Tv', 'Coffee', 'Bath', 'Bed', 'Car', 'Snowflake', 'Sun', 'Wind', 'Waves', 'TreePine'];

  const resetForm = () => {
    setForm({ name: '', icon: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (amenity) => {
    setForm({ name: amenity.name, icon: amenity.icon });
    setEditingId(amenity.amenity_id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.icon) {
      setError('Name and icon are required');
      return;
    }
    try {
      if (editingId) {
        const updated = await adminUpdateAmenity(editingId, form);
        setAmenities(amenities.map(a => a.amenity_id === editingId ? updated : a));
        setSuccess('Amenity updated');
      } else {
        const created = await adminCreateAmenity(form);
        setAmenities([...amenities, created]);
        setSuccess('Amenity created');
      }
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save amenity');
    }
  };

  const handleToggle = async (amenityId, isActive) => {
    try {
      await adminUpdateAmenity(amenityId, { is_active: !isActive });
      setAmenities(amenities.map(a => a.amenity_id === amenityId ? { ...a, is_active: !isActive } : a));
      setSuccess('Amenity updated');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update amenity');
    }
  };

  const handleDelete = async (amenityId) => {
    if (!window.confirm('Delete this amenity?')) return;
    try {
      await adminDeleteAmenity(amenityId);
      setAmenities(amenities.filter(a => a.amenity_id !== amenityId));
      setSuccess('Amenity deleted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete amenity');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Amenities ({amenities.length})</h3>
          <p className="text-stone-400 text-sm">These appear in the About section on the homepage</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-500">
          <Plus className="w-4 h-4 mr-2" /> Add Amenity
        </Button>
      </div>

      {showForm && (
        <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-6">
          <h4 className="text-white font-medium mb-4">{editingId ? 'Edit Amenity' : 'Add New Amenity'}</h4>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-stone-400">Name *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                placeholder="e.g., Fully Equipped Kitchen"
                className="bg-stone-900 border-stone-600 text-white mt-1" 
              />
            </div>
            <div>
              <Label className="text-stone-400">Icon *</Label>
              <select 
                value={form.icon} 
                onChange={(e) => setForm({ ...form, icon: e.target.value })} 
                className="w-full mt-1 bg-stone-900 border border-stone-600 text-white rounded-md px-3 py-2"
              >
                <option value="">Select icon...</option>
                {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-500">
              <Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Create'}
            </Button>
            <Button onClick={resetForm} variant="outline" className="border-stone-600 text-stone-300">Cancel</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {amenities.map((amenity) => (
          <div key={amenity.amenity_id} className={`bg-stone-800/50 border border-stone-700/50 rounded-xl p-4 ${!amenity.is_active ? 'opacity-50' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-emerald-400 text-xs">{amenity.icon}</span>
              <div className="flex gap-1">
                <button onClick={() => handleToggle(amenity.amenity_id, amenity.is_active)} className={`p-1 rounded ${amenity.is_active ? 'text-emerald-400' : 'text-stone-500'}`}>
                  {amenity.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                </button>
                <button onClick={() => handleEdit(amenity)} className="text-blue-400 hover:text-blue-300 p-1">
                  <Edit2 className="w-3 h-3" />
                </button>
                <button onClick={() => handleDelete(amenity.amenity_id)} className="text-red-400 hover:text-red-300 p-1">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
            <p className="text-white text-sm font-medium">{amenity.name}</p>
          </div>
        ))}
      </div>

      {amenities.length === 0 && (
        <div className="text-center py-12 bg-stone-800/30 rounded-xl border border-dashed border-stone-700">
          <Package className="w-12 h-12 text-stone-600 mx-auto mb-3" />
          <p className="text-stone-400">No amenities yet. Add your first amenity!</p>
        </div>
      )}
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ settings, setSettings, setError, setSuccess, gallery }) => {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await adminUpdateSettings(form);
      setSettings(updated);
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const selectImageFromGallery = (url) => {
    setForm({ ...form, hero_image: url });
    setShowImagePicker(false);
  };

  return (
    <div className="max-w-3xl">
      {/* Hero Image Section */}
      <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-6">Hero Background Image</h3>
        
        <div className="mb-4">
          <Label className="text-stone-400 mb-2 block">Current Hero Image</Label>
          <div className="relative rounded-xl overflow-hidden h-48 bg-stone-900">
            {form.hero_image ? (
              <img src={form.hero_image} alt="Hero background" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-500">No image selected</div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent" />
          </div>
        </div>

        <div className="mb-4">
          <Label className="text-stone-400">Image URL</Label>
          <Input
            value={form.hero_image || ''}
            onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
            placeholder="https://..."
            className="bg-stone-900 border-stone-600 text-white mt-1"
          />
          <p className="text-xs text-stone-500 mt-1">Enter a URL or select from your gallery below</p>
        </div>

        <Button type="button" onClick={() => setShowImagePicker(!showImagePicker)} variant="outline" className="border-stone-600 text-stone-300 hover:bg-stone-700">
          <Image className="w-4 h-4 mr-2" />
          {showImagePicker ? 'Hide Gallery' : 'Select from Gallery'}
        </Button>

        {showImagePicker && gallery && gallery.length > 0 && (
          <div className="mt-4 p-4 bg-stone-900/50 rounded-xl">
            <p className="text-sm text-stone-400 mb-3">Click an image to select it:</p>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
              {gallery.map((img) => (
                <button
                  key={img.image_id}
                  onClick={() => selectImageFromGallery(img.url)}
                  className={`relative rounded-lg overflow-hidden h-20 transition-all ${
                    form.hero_image === img.url ? 'ring-2 ring-emerald-400' : 'hover:ring-2 hover:ring-stone-500'
                  }`}
                >
                  <img src={img.url} alt={img.caption || ''} className="w-full h-full object-cover" />
                  {form.hero_image === img.url && (
                    <div className="absolute inset-0 bg-emerald-500/30 flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-6">Pricing</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <Label className="text-stone-400">Weekday Price ($)</Label>
            <Input type="number" value={form.weekday_price || ''} onChange={(e) => setForm({ ...form, weekday_price: parseFloat(e.target.value) })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div>
            <Label className="text-stone-400">Weekend Multiplier</Label>
            <Input type="number" step="0.01" value={form.weekend_multiplier || ''} onChange={(e) => setForm({ ...form, weekend_multiplier: parseFloat(e.target.value) })} className="bg-stone-900 border-stone-600 text-white mt-1" />
            <p className="text-xs text-stone-500 mt-1">e.g., 1.30 = 30% increase</p>
          </div>
          <div>
            <Label className="text-stone-400">Minimum Nights</Label>
            <Input type="number" value={form.minimum_nights || ''} onChange={(e) => setForm({ ...form, minimum_nights: parseInt(e.target.value) })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
        </div>
      </div>

      <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-6">Contact Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-stone-400">Email</Label>
            <Input value={form.contact_email || ''} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div>
            <Label className="text-stone-400">Phone</Label>
            <Input value={form.contact_phone || ''} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-stone-400">Address</Label>
            <Input value={form.contact_address || ''} onChange={(e) => setForm({ ...form, contact_address: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
        </div>
      </div>

      <div className="bg-stone-800/50 border border-stone-700/50 rounded-xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-6">Homepage Content</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-stone-400">Hero Tagline</Label>
            <Input value={form.hero_tagline || ''} onChange={(e) => setForm({ ...form, hero_tagline: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div>
            <Label className="text-stone-400">Hero Subtitle</Label>
            <Input value={form.hero_subtitle || ''} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div>
            <Label className="text-stone-400">About Section Title</Label>
            <Input value={form.about_title || ''} onChange={(e) => setForm({ ...form, about_title: e.target.value })} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
          <div>
            <Label className="text-stone-400">About Description</Label>
            <Textarea value={form.about_description || ''} onChange={(e) => setForm({ ...form, about_description: e.target.value })} rows={4} className="bg-stone-900 border-stone-600 text-white mt-1" />
          </div>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white px-8">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : 'Save Settings'}
      </Button>
    </div>
  );
};

export default AdminDashboard;
